import { Fragment, useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

function initialForm(fields, item) {
  const out = {};
  for (const f of fields) {
    if (item) {
      if (f.type === "bool") out[f.key] = !!item[f.key];
      else if (f.type === "datetime") out[f.key] = item[f.key] ? item[f.key].slice(0, 16) : "";
      else if (f.type === "date") out[f.key] = item[f.key] ? item[f.key].slice(0, 10) : "";
      else out[f.key] = item[f.key] ?? "";
    } else {
      out[f.key] = f.type === "bool" ? f.default ?? false : "";
    }
  }
  return out;
}

function Field({ field, value, onChange }) {
  const common = { name: field.key, value: field.type === "bool" ? undefined : value };
  if (field.type === "textarea") {
    return <textarea {...common} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === "bool") {
    return (
      <input type="checkbox" name={field.key} style={{ width: "auto" }} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    );
  }
  if (field.type === "select") {
    return (
      <select {...common} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  const inputType = field.type === "datetime" ? "datetime-local" : field.type === "date" ? "date" : field.type === "number" ? "number" : "text";
  return <input type={inputType} {...common} required={field.required} onChange={(e) => onChange(e.target.value)} />;
}

function EditForm({ fields, item, onSave, onCancel }) {
  const [form, setForm] = useState(() => initialForm(fields, item));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const err = await onSave(form);
    setBusy(false);
    if (err) setError(err);
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        {fields.map((f) => (
          <label className="field" key={f.key} style={f.type === "textarea" ? { gridColumn: "1 / -1" } : {}}>
            {f.label}
            {f.type === "bool" ? (
              <span style={{ paddingTop: 4 }}>
                <Field field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
              </span>
            ) : (
              <Field field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
            )}
          </label>
        ))}
      </div>
      <div className="field-actions">
        <button className="btn-primary" style={{ width: "auto" }} type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="btn-ghost" type="button" onClick={onCancel}>Cancel</button>
        {error && <span className="err">{error}</span>}
      </div>
    </form>
  );
}

function display(field, item) {
  const v = item[field.key];
  if (field.type === "bool") return v ? "✓" : "–";
  if (field.type === "datetime") return v ? v.slice(0, 16).replace("T", " ") : "—";
  if (field.type === "date") return v ? v.slice(0, 10) : "—";
  return v === null || v === undefined || v === "" ? "—" : String(v);
}

export function ContentManager({
  chapterId,
  config,
  canManage,
  detail: Detail,
  detailLabel,
  onChange,
}) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | id | null
  const [detailId, setDetailId] = useState(null);
  const fields = config.fields;
  const columns = config.columns.map((k) => fields.find((f) => f.key === k));

  function load() {
    api(`/api/chapters/${chapterId}/content/${config.kind}`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data) => setItems(data.items));
  }
  useEffect(load, [chapterId, config.kind]);

  async function save(form) {
    const isNew = editing === "new";
    const url = isNew
      ? `/api/chapters/${chapterId}/content/${config.kind}`
      : `/api/content/${config.kind}/${editing}`;
    const res = await api(url, { method: isNew ? "POST" : "PATCH", body: JSON.stringify(form) });
    if (res.ok) {
      setEditing(null);
      load();
      onChange?.();
      return null;
    }
    return (await res.json().catch(() => ({}))).error || "Could not save.";
  }
  async function remove(item) {
    if (!window.confirm(`Delete this ${config.singular}?`)) return;
    await api(`/api/content/${config.kind}/${item.id}`, { method: "DELETE" });
    load();
    onChange?.();
  }

  if (items === null) return <p className="muted">Loading…</p>;
  const editingItem = editing && editing !== "new" ? items.find((i) => i.id === editing) : null;

  return (
    <>
      <div className="section-head">
        <h3>{config.label} · {items.length}</h3>
        {canManage && editing === null && (
          <button className="btn-primary" style={{ width: "auto" }} onClick={() => setEditing("new")} type="button">
            Add {config.singular}
          </button>
        )}
      </div>

      {editing === "new" && (
        <EditForm fields={fields} item={null} onSave={save} onCancel={() => setEditing(null)} />
      )}

      <div style={{ marginTop: 12 }}>
        {items.length ? (
          <table className="tbl">
            <thead>
              <tr>
                {columns.map((f) => <th key={f.key}>{f.label}</th>)}
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const locked = config.lockedField && item[config.lockedField];
                return (
                <Fragment key={item.id}>
                  <tr>
                    {columns.map((f) => <td key={f.key}>{display(f, item)}</td>)}
                    {canManage && (
                      <td className="row-actions">
                        {locked ? (
                          <span className="muted" style={{ fontSize: 12 }}>linked · {locked}</span>
                        ) : (
                          <>
                            {Detail && (
                              <button className="btn-ghost" type="button" onClick={() => setDetailId(detailId === item.id ? null : item.id)}>
                                {detailId === item.id ? "Close" : (detailLabel || "Details")}
                              </button>
                            )}
                            <button className="btn-ghost" type="button" onClick={() => setEditing(editing === item.id ? null : item.id)}>
                              {editing === item.id ? "Close" : "Edit"}
                            </button>
                            <button className="btn-danger" type="button" onClick={() => remove(item)}>Delete</button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                  {editing === item.id && (
                    <tr>
                      <td colSpan={columns.length + 1} style={{ background: "var(--surface-2)" }}>
                        <EditForm fields={fields} item={editingItem} onSave={save} onCancel={() => setEditing(null)} />
                      </td>
                    </tr>
                  )}
                  {Detail && detailId === item.id && (
                    <tr>
                      <td colSpan={columns.length + 1} style={{ background: "var(--surface-2)" }}>
                        <Detail item={item} chapterId={chapterId} canManage={canManage} />
                      </td>
                    </tr>
                  )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="empty">No {config.label.toLowerCase()} yet.</p>
        )}
      </div>
    </>
  );
}
